/**
 * Task List component with CRUD operations
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/themes.css';

const TaskList = () => {
  const {
    tasks,
    loadTasks,
    setSelectedTask
  } = useAdmin();

  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const limit = 20;

  useEffect(() => {
    loadTasksData();
  }, [page]);

  const loadTasksData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await loadTasks(page, limit);
      setTotalTasks(response.total);
    } catch (err) {
      setError('Failed to load tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleCreateNew = () => {
    setSelectedTask(null);
    setShowCreateForm(true);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'var(--color-success)';
      case 'medium': return 'var(--color-warning)';
      case 'hard': return 'var(--color-accent)';
      case 'expert': return 'var(--color-error)';
      default: return 'var(--color-outline)';
    }
  };

  const totalPages = Math.ceil(totalTasks / limit);

  return (
    <div className="themed-background" style={{ padding: '1rem', minHeight: '100vh' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <h2 style={{ color: 'var(--color-background-text)' }}>Tasks ({totalTasks})</h2>

        <button
          onClick={handleCreateNew}
          className="themed-button"
          style={{
            padding: '0.5rem 1rem',
            fontSize: '1rem'
          }}
        >
          + Create New Task
        </button>
      </div>

      {error && (
        <div className="status-error themed-surface" style={{
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid var(--color-error)'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--color-background-text)'
        }}>
          Loading tasks...
        </div>
      ) : (
        <>
          {/* Task Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {tasks.map(task => (
              <div
                key={task.id}
                onClick={() => handleTaskClick(task)}
                className="themed-surface"
                style={{
                  padding: '1.5rem',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px var(--shadow-color)',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.boxShadow = '0 4px 8px var(--shadow-color)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.boxShadow = '0 2px 4px var(--shadow-color)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.1rem',
                    color: 'var(--color-surface-text)'
                  }}>
                    {task.title_en}
                  </h3>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    backgroundColor: getDifficultyColor(task.difficulty),
                    color: 'var(--color-primary-text)',
                    fontWeight: 'bold'
                  }}>
                    {task.difficulty?.toUpperCase()}
                  </span>
                </div>

                <div style={{
                  marginBottom: '0.5rem',
                  color: 'var(--color-surface-text)'
                }}>
                  <strong>Category:</strong>
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'var(--color-outline)',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    color: 'var(--color-primary-text)'
                  }}>
                    {task.category}
                  </span>
                </div>

                <div style={{
                  marginBottom: '1rem',
                  color: 'var(--color-surface-text)'
                }}>
                  <strong>ID:</strong> {task.id}
                </div>

                {task.text_en && (
                  <p style={{
                    color: 'var(--color-surface-text)',
                    opacity: 0.8,
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                    margin: 0,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {task.text_en}
                  </p>
                )}

                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <small style={{ color: 'var(--color-surface-text)', opacity: 0.7 }}>
                    Click to edit
                  </small>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTaskClick(task);
                    }}
                    className="themed-button"
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem'
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {tasks.length === 0 && (
            <div className="themed-surface" style={{
              textAlign: 'center',
              padding: '3rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px var(--shadow-color)'
            }}>
              <h3 style={{ color: 'var(--color-surface-text)' }}>No Tasks Found</h3>
              <p style={{
                color: 'var(--color-surface-text)',
                opacity: 0.7,
                marginBottom: '1rem'
              }}>
                Get started by creating your first task.
              </p>
              <button
                onClick={handleCreateNew}
                className="themed-button"
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem'
                }}
              >
                Create First Task
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>

              <span className="themed-surface" style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                border: '1px solid var(--border-color)'
              }}>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskList;